/**
 * Gravitec Push Notifications Service
 * 
 * Handles web push notification subscription and management via Gravitec
 */

import api from './api';

// Gravitec App Key (public)
const GRAVITEC_APP_KEY = 'c29a7c8975dfd68698e342d1448f846c';

let gravitecInitialized = false;

/**
 * Initialize Gravitec SDK
 * Note: The main service worker (sw.js) already imports the Gravitec push-worker.js
 */
export async function initGravitec() {
  if (typeof window === 'undefined' || gravitecInitialized) return;
  
  // Initialize Gravitec SDK when available
  const initSDK = () => {
    if (window.Gravitec) {
      window.Gravitec('init', {
        appKey: GRAVITEC_APP_KEY,
        autoSubscribe: false,
        serviceWorkerPath: '/sw.js', // Use our main service worker
      });
      gravitecInitialized = true;
      console.log('Gravitec SDK initialized');
    } else {
      // SDK not loaded yet, try again
      setTimeout(initSDK, 500);
    }
  };
  
  initSDK();
}

/**
 * Check if push notifications are supported
 */
export function isPushSupported() {
  return 'PushManager' in window && 'serviceWorker' in navigator && 'Notification' in window;
}

/**
 * Get current push notification permission status
 */
export function getPushPermissionStatus() {
  if (!isPushSupported()) return 'unsupported';
  return Notification.permission; // 'granted', 'denied', or 'default'
}

/**
 * Request push notification permission and subscribe
 */
export async function subscribeToPush(userEmail) {
  if (!isPushSupported()) {
    return { success: false, error: 'Push notifications not supported' };
  }
  
  try {
    // Request notification permission
    const permission = await Notification.requestPermission();
    
    if (permission !== 'granted') {
      return { success: false, error: 'Permission denied' };
    }
    
    // Try Gravitec SDK first
    if (window.Gravitec) {
      return new Promise((resolve) => {
        window.Gravitec('subscribe', {
          onSubscribed: async (subscriberId) => {
            console.log('Subscribed to Gravitec:', subscriberId);
            
            // Register with backend to associate with user account
            try {
              await api.post('/push/register', {
                subscriber_id: subscriberId,
                alias: userEmail
              });
              resolve({ success: true, subscriberId });
            } catch (err) {
              console.error('Failed to register subscriber:', err);
              resolve({ success: true, subscriberId }); // Still success, just couldn't register alias
            }
          },
          onError: (error) => {
            console.error('Gravitec subscription error:', error);
            resolve({ success: false, error: error?.message || 'Subscription failed' });
          }
        });
        
        // Timeout fallback in case Gravitec doesn't respond
        setTimeout(() => {
          resolve({ success: false, error: 'Subscription timeout' });
        }, 10000);
      });
    }
    
    // Fallback: Use native Push API if Gravitec SDK not available
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      const registration = await navigator.serviceWorker.ready;
      
      // Subscribe to push using VAPID (requires server-side setup)
      // For now, just register with backend using a generated ID
      const subscriberId = `native-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      
      try {
        await api.post('/push/register', {
          subscriber_id: subscriberId,
          alias: userEmail
        });
        return { success: true, subscriberId, native: true };
      } catch (err) {
        console.error('Failed to register native subscriber:', err);
        return { success: false, error: 'Failed to register' };
      }
    }
    
    return { success: false, error: 'Push service not available' };
  } catch (error) {
    console.error('Push subscription error:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Unsubscribe from push notifications
 */
export async function unsubscribeFromPush() {
  if (window.Gravitec) {
    window.Gravitec('unsubscribe');
    return { success: true };
  }
  return { success: false, error: 'Gravitec SDK not loaded' };
}

/**
 * Send a test push notification to current user
 */
export async function sendTestPush() {
  try {
    const response = await api.post('/push/send', {
      title: '🔔 Test Notification',
      message: 'Push notifications are working! You\'ll receive reminders and backup alerts here.',
      url: '/'
    });
    return response.data;
  } catch (error) {
    console.error('Failed to send test push:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Trigger a backup reminder push notification
 */
export async function sendBackupReminderPush() {
  try {
    const response = await api.post('/push/backup-reminder');
    return response.data;
  } catch (error) {
    console.error('Failed to send backup reminder push:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Get push notification status
 */
export async function getPushStatus() {
  try {
    const response = await api.get('/push/status');
    return {
      ...response.data,
      permission: getPushPermissionStatus(),
      supported: isPushSupported()
    };
  } catch (error) {
    return {
      configured: false,
      subscriber_registered: false,
      permission: getPushPermissionStatus(),
      supported: isPushSupported()
    };
  }
}

export default {
  initGravitec,
  isPushSupported,
  getPushPermissionStatus,
  subscribeToPush,
  unsubscribeFromPush,
  sendTestPush,
  sendBackupReminderPush,
  getPushStatus
};
